import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { TabsContainer } from './Tabs.styles';
import TabPanel from './TabPanel';
import TabList from './TabList';

interface props {
  canClick?: boolean;
  activeTab?: string;
  colorPanel?: boolean;
  children: JSX.Element[] | any[];
  onSetCurrentTab?: (value: string) => void;
}

const Tabs: React.FC<props> = ({
  onSetCurrentTab,
  colorPanel,
  activeTab,
  canClick,
  children
}) => {
  const [currentTab, setCurrentTab] = useState<string | null>(null);

  const handleChangeTab = useCallback(
    (value: string) => {
      if (value !== '-1' && !canClick) return;
      value !== '-1' && setCurrentTab(value);
      onSetCurrentTab && setTimeout(() => onSetCurrentTab(value));
    },
    [setCurrentTab, canClick]
  );

  const mappedChildren = useMemo(
    () =>
      children
        .filter(({ type }) => type === TabPanel || type === TabList)
        .map((element: JSX.Element, i: number) => {
          const props = { currentTab, onSetCurrentTab: handleChangeTab };

          if (element.type === TabList) {
            let children = element.props.children;

            children = !Array.isArray(children)
              ? React.cloneElement(children, props)
              : children.map((item: JSX.Element, i: number) =>
                  React.cloneElement(item, { key: `tab-${i}`, ...props })
                );

            if (currentTab === null) {
              const _value =
                children?.[1]?.props?.value || children?.props?.value;

              _value && handleChangeTab(_value);
            }

            return React.cloneElement(element, {
              key: 'tab-panel',
              children
            });
          }

          return element.type !== TabPanel
            ? element
            : React.cloneElement(element, {
                key: `tab-panel-${i}`,
                currentTab
              });
        }),
    [children, handleChangeTab, currentTab]
  );

  useEffect(() => {
    if (activeTab && activeTab !== currentTab) {
      setCurrentTab(activeTab);
    }
  }, [activeTab]);

  return <TabsContainer panel={colorPanel}>{mappedChildren}</TabsContainer>;
};

export default memo(Tabs);
